import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Shield, Clock, Sparkles, Check, HelpCircle } from 'lucide-react';
import { serviceSteps, contactInfo } from '../data';
import SEO from '../components/SEO';

export default function Service() {
  return (
    <div className="pt-24 md:pt-40 xl:pt-28 pb-16">
      <SEO
        title="Service & Vedligeholdelse af Stenbelægning | Stenbelægning.dk"
        description="Professionel service og vedligeholdelse af din helstøbte stenbelægning. Rengøring, re-sealing og genopfriskning forstærker levetiden og bevarer det flotte udtryk."
      />
      {/* Header Section */}
      <section className="bg-stone-50 py-16 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-stone-900 tracking-tight mt-2">
              Service & Vedligeholdelse
            </h1>
            <p className="text-xl text-stone-600 mt-4 font-medium">
              Vedligeholdelsesaftale til din belægning
            </p>
            <p className="text-stone-500 mt-4 leading-relaxed">
              Selvom helstøbt stenbelægning kræver minimal vedligeholdelse, kan en serviceaftale forlænge levetiden og sikre, at overfladen bliver ved med at fremstå flot.
            </p>
          </div>
        </div>
      </section>

      {/* Why Service Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-stone-900">Hvorfor vælge en serviceaftale?</h2>
              <p className="text-stone-600 leading-relaxed">
                En helstøbt stenbelægning er utrolig stærk og modstandsdygtig. Men ligesom alle andre udendørs overflader udsættes den for det skiftende danske vejr med regn, frost, sollys og nedfaldne blade. 
              </p>
              <p className="text-stone-600 leading-relaxed">
                Med en årlig eller halvårlig serviceaftale sørger vi for, at din belægning bliver renset i dybden med professionelt udstyr, og at eventuelle slidte områder får en opfriskende topcoat. Det genskaber stenenes dybe glans og forsegler overfladen på ny.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="flex items-center gap-2 text-stone-700 font-semibold">
                  <Check className="h-5 w-5 text-stone-900 shrink-0" />
                  <span>Forlænget levetid</span>
                </div>
                <div className="flex items-center gap-2 text-stone-700 font-semibold">
                  <Check className="h-5 w-5 text-stone-900 shrink-0" />
                  <span>Smuk glans år efter år</span>
                </div>
                <div className="flex items-center gap-2 text-stone-700 font-semibold">
                  <Check className="h-5 w-5 text-stone-900 shrink-0" />
                  <span>Professionelt udstyr</span>
                </div>
                <div className="flex items-center gap-2 text-stone-700 font-semibold">
                  <Check className="h-5 w-5 text-stone-900 shrink-0" />
                  <span>Forebyggelse af skader</span>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden shadow-xl aspect-video lg:aspect-square">
              <img
                src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/d9e66450-9d19-4e2f-89b7-8625a6886679.jpg"
                alt="Nærbillede af ren og flot stenbelægning"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service Agreement Contents */}
      <section className="py-16 border-t border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Image */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="relative overflow-hidden shadow-xl aspect-[4/3] lg:aspect-[3/4]">
                <img
                  src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/6055090a-18d2-4c2c-9d28-28de4d83c95a.jpg"
                  alt="Professionel service og vedligeholdelse af stenbelægning"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right Column: Text & Vertical Boxes */}
            <div className="lg:col-span-7 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-stone-900">Hvad omfatter en serviceaftale?</h2>
                <p className="text-stone-500 mt-2">
                  Vi gennemgår og plejer din belægning systematisk, så du ikke skal røre en finger.
                </p>
              </div>

              <div className="space-y-4">
                {serviceSteps.map((step, idx) => (
                  <div 
                    key={idx} 
                    className="bg-white p-6 border border-stone-200/80 shadow-sm flex gap-5 items-start rounded-lg border-l-4 border-l-stone-300"
                  >
                    <div className="h-10 w-10 bg-stone-900 text-white flex items-center justify-center font-mono font-bold shrink-0 rounded-lg shadow-inner">
                      {String(idx + 1).padStart(2, '0')}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-stone-900 mb-1">
                        {step.title}
                      </h3>
                      <p className="text-stone-600 text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ / Self Maintenance Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-stone-50 p-8 sm:p-12 border border-stone-200">
            <div className="max-w-3xl mx-auto text-center">
              <HelpCircle className="h-12 w-12 text-stone-900 mx-auto mb-4" />
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">Hvordan vedligeholder jeg det selv?</h2>
              <p className="text-stone-600 mt-4 leading-relaxed">
                I hverdagen kræver din helstøbte stenbelægning næsten ingenting. Du kan blot feje den for blade og snavs, eller spule den med en almindelig haveslange efter behov. Da overfladen er fugefri, slipper du helt for at luge ukrudt eller fylde fuge-sand på.
              </p>
              <div className="mt-8">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold bg-stone-900 text-white hover:bg-stone-800 hover:-translate-y-1 transition-all duration-300"
                >
                  Hør mere om serviceaftaler
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
